import { ChangeEvent, FormEvent, useState } from 'react'
import styled from 'styled-components'
import { useStore } from 'store'
import { validFileExtensions } from 'utils/validators'
import copy from 'copy.json'
import useIsAdminPage from 'hooks/useIsAdminPage'

import StyledModal from 'components/layout/StyledModal'
import FileUploadWrapper from 'components/layout/FileUploadWrapper'
import Input from 'components/Input'
import Button from 'components/Button'
import Spinner from 'components/Spinner'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { energyProfileCsvGuide } from 'components/csvFormatGuides'

const text = copy.modal.profile_file

type Props = {
  title: string
  description: string
  fieldErrorName: string
  isModalOpen: boolean
  onModalClose: () => void
  onModalConfirmation: (profileFile: File | null, reason: string) => Promise<boolean>
}

export default function UpdateProfileFileModal({
  title,
  description,
  fieldErrorName,
  isModalOpen,
  onModalClose,
  onModalConfirmation
}: Props) {
  const [profileFile, setProfileFile] = useState<File | null>(null)
  const [requestReason, setRequestReason] = useState<string>('')
  const { fieldErrors, clearFieldErrors } = useStore()
  const [loading, setLoading] = useState(false)
  const isAdminPage = useIsAdminPage()

  const handleRequestReasonChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    clearFieldErrors()
    setRequestReason(event.target.value)
  }

  const onHandleFileChange = (event: FormEvent<HTMLInputElement> | null) => {
    clearFieldErrors()
    const currentTarget = event?.currentTarget

    if (currentTarget?.files) {
      const file = currentTarget.files[0]
      setProfileFile(file)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    const success = await onModalConfirmation(profileFile, requestReason)
    setLoading(false)

    if (success) handleClose()
  }

  const handleClose = () => {
    setRequestReason('')
    setProfileFile(null)
    clearFieldErrors()
    onModalClose()
  }

  const isButtonDisabled = !profileFile || (isAdminPage && !requestReason)

  return (
    <StyledModal isOpen={isModalOpen} onClose={handleClose}>
      <Form onSubmit={handleSubmit}>
        <h3>{title}</h3>

        {description && (
          <p>
            {text.confirm_change_profile_file_paragraph_1}
            <br />
            {text.confirm_change_profile_file_paragraph_2}
          </p>
        )}

        {description === 'change_and_deactivate_offer' && (
          <p>
            <strong>{text.confirm_change_profile_file_paragraph_deactivate_offer_1}</strong>
            <br />
            {text.confirm_change_profile_file_paragraph_deactivate_offer_2}
          </p>
        )}

        {description === 'change_and_deactivate_demand' && (
          <p>
            <strong>{text.confirm_change_profile_file_paragraph_deactivate_demand_1}</strong>
            <br />
            {text.confirm_change_profile_file_paragraph_deactivate_demand_2}
          </p>
        )}

        <ReasonWrapper>
          {isAdminPage && (
            <Details>
              <p>{text.reason}</p>

              <TextareaWrapper>
                <textarea value={requestReason} onChange={handleRequestReasonChange} />
                {fieldErrors.reason && <span>{fieldErrors.reason}</span>}
              </TextareaWrapper>
            </Details>
          )}

          <FileUploadWrapper style={{ padding: 0 }}>
            <Input
              name={fieldErrorName}
              type="file"
              onChange={onHandleFileChange}
              accept={validFileExtensions.join(',')}
              style={{ width: '100%' }}
            />
            <CsvFormatGuide definition={energyProfileCsvGuide} />
          </FileUploadWrapper>

          {fieldErrors[fieldErrorName] && <Error>{fieldErrors[fieldErrorName]}</Error>}
        </ReasonWrapper>

        <Buttons>
          <Button type="submit" disabled={isButtonDisabled}>
            {loading ? <Spinner size={16} /> : copy.common.button.add_new_file}
          </Button>

          <Button type="button" secondary onClick={handleClose}>
            {copy.common.button.cancel}
          </Button>
        </Buttons>
      </Form>
    </StyledModal>
  )
}

const ReasonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 100%;
  padding: 16px max(8px, calc((100% - 600px) / 2));

  background: var(--very-light-blue);
`
const Details = styled.div`
  display: flex;
  gap: 20px;

  font: var(--600-13);

  p:first-of-type {
    width: 90px;
    text-align: left;
  }
`

const TextareaWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  textarea {
    align-self: stretch;
    min-height: 128px;
    padding: 4px;
    border: 1px solid var(--light-gray);
    border-radius: 4px;
    font: var(--400-13);
  }

  span {
    color: var(--red);
    font: var(--400-11);
  }
`

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  width: 100%;
  max-width: 424px;
  margin-top: 8px;
  margin-bottom: 24px;

  button {
    width: 100%;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  width: 100%;

  p {
    max-width: 424px;
  }
`

const Error = styled.span`
  color: var(--red);
  font: var(--400-11);
`
